import { apiClient, ApiResponse } from './api';
import type { Profile } from '@/store/profile.store';
import { useProfileStore } from '@/store/profile.store';
import { useAuthStore } from '@/store/auth.store';
import { localProfileService } from './local/profile.local';
import { extensionSyncService } from './extension-sync.service';

export interface ProfileUpdate {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string | null;
  location?: string | null;
  summary?: string | null;
}

export interface ProfilePreview {
  firstName: string;
  lastName: string;
  email: string;
  location: string | null;
  experienceYears: number;
  skillsCount: number;
}

export type ProfileResponse = Profile;

// Helper to create local profile ID
const generateLocalId = () => `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const normalizeSkills = (skills: unknown): string[] => {
  // Handle {technical: string[], soft: string[]} format from backend
  if (skills && typeof skills === 'object' && !Array.isArray(skills)) {
    const obj = skills as Record<string, unknown>;
    const result: string[] = [];
    for (const val of Object.values(obj)) {
      if (Array.isArray(val)) {
        for (const item of val) {
          if (typeof item === 'string' && item.trim()) result.push(item.trim());
        }
      }
    }
    return result;
  }
  if (!Array.isArray(skills)) return [];
  return skills
    .map((s) => {
      if (typeof s === 'string') return s;
      if (s && typeof s === 'object') {
        // @ts-ignore
        return s.name || s.skill || s.title || '';
      }
      return '';
    })
    .map((s) => s?.toString().trim())
    .filter(Boolean);
};

export const normalizeProfile = (profile: Profile): Profile => {
  const raw = profile as any;
  const pi = raw.personalInfo || {};

  // Flatten personalInfo into top-level fields (backend stores nested, store expects flat)
  const fullName = pi.fullName || raw.fullName || '';
  const nameParts = fullName.split(/\s+/);

  return {
    ...profile,
    // Flatten personalInfo → top-level fields
    firstName: raw.firstName || nameParts[0] || '',
    lastName: raw.lastName || nameParts.slice(1).join(' ') || '',
    email: raw.email || pi.email || '',
    phone: raw.phone || pi.phone || null,
    location: raw.location || pi.location || null,
    // Normalize arrays
    skills: normalizeSkills(raw.skills),
    experience: Array.isArray(raw.experience) ? raw.experience.map((e: any) => ({
      id: e.id || `exp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      company: e.company || '',
      position: e.position || e.title || '',
      startDate: e.startDate || '',
      endDate: e.endDate || null,
      description: e.description || '',
      current: e.current || e.endDate === 'present' || false,
    })) : [],
    education: Array.isArray(raw.education) ? raw.education.map((e: any) => ({
      id: e.id || `edu-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      institution: e.institution || '',
      degree: e.degree || '',
      fieldOfStudy: e.fieldOfStudy || e.field || '',
      startDate: e.startDate || '',
      endDate: e.endDate || null,
      current: e.current || false,
    })) : [],
    certifications: Array.isArray(raw.certifications) ? raw.certifications.map((c: any) => ({
      id: c.id || `cert-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: c.name || '',
      issuer: c.issuer || '',
      issueDate: c.issueDate || c.date || '',
      expiryDate: c.expiryDate || null,
      credentialId: c.credentialId || null,
    })) : [],
    languages: Array.isArray(raw.languages) ? raw.languages : (Array.isArray(raw.skills?.languages) ? raw.skills.languages : []),
    // Summary
    summary: raw.summary || pi.summary || null,
  };
};

export const profileService = {
  /**
   * Get user's profile (works in both local and server mode)
   */
  async getProfile(): Promise<Profile> {
    const { mode, isAuthenticated } = useAuthStore.getState();
    const { profile, setLocalMode, setProfile } = useProfileStore.getState();

    // Local mode path — only when not authenticated
    if (!isAuthenticated && mode === 'local') {
      setLocalMode(true);
      if (profile) return profile;
      const local = await localProfileService.getProfile();
      const normalized = normalizeProfile(local as any);
      setProfile(normalized);
      return normalized;
    }

    // Try to get from server
    try {
      const response = await apiClient.get<ApiResponse<Profile>>('/api/v1/profile');
      const serverProfile = normalizeProfile(response.data.data as any);

      // Switch to server mode
      setLocalMode(false);
      setProfile(serverProfile);

      return serverProfile;
    } catch (error: any) {
      const status = error?.response?.status;

      // Profile doesn't exist on server yet — create it from local data
      if (status === 404 && profile) {
        console.log('[ProfileService] No server profile found, syncing local profile to server...');
        try {
          const serverProfile = await profileService.syncToServer(profile);
          const normalized = normalizeProfile(serverProfile as any);
          setLocalMode(false);
          setProfile(normalized);
          return normalized;
        } catch (syncError) {
          console.warn('[ProfileService] Auto-sync to server failed, using local profile:', syncError);
          return profile;
        }
      }

      // Other errors — return local profile if available
      if (profile) {
        return profile;
      }
      throw error;
    }
  },

  /**
   * Create new profile (local or server)
   */
  async createProfile(data: Partial<Profile>): Promise<Profile> {
    const { mode, isAuthenticated } = useAuthStore.getState();
    const { setLocalMode, setProfile } = useProfileStore.getState();

    if (!isAuthenticated && mode === 'local') {
      // Create local profile
      const localProfile: Profile = normalizeProfile({
        id: generateLocalId(),
        userId: 'local-user',
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        email: data.email || '',
        phone: data.phone || null,
        location: data.location || null,
        summary: data.summary || null,
        experience: data.experience || [],
        education: data.education || [],
        skills: data.skills || [],
        certifications: data.certifications || [],
        languages: data.languages || [],
        createdAt: new Date(),
        updatedAt: new Date(),
      } as Profile);

      await localProfileService.saveProfile(localProfile as any);

      // Синхронизировать с extension
      console.log('🔄 [ProfileService] Syncing new profile to extension...');
      await extensionSyncService.syncProfileToExtension(localProfile);

      setProfile(localProfile);
      setLocalMode(true);
      return localProfile;
    }

    // Create on server — convert local profile format to backend schema
    const normalizeDate = (d: string | null | undefined): string => {
      if (!d) return '2020-01';
      const trimmed = d.trim();
      if (/^\d{4}-\d{2}$/.test(trimmed)) return trimmed;
      if (/^\d{4}$/.test(trimmed)) return `${trimmed}-01`;
      const parsed = new Date(trimmed);
      if (!isNaN(parsed.getTime())) {
        return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}`;
      }
      return '2020-01';
    };

    const backendData = {
      personalInfo: {
        fullName: `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Unknown',
        email: data.email || 'user@example.com',
        phone: data.phone || undefined,
        location: data.location || undefined,
      },
      summary: data.summary || undefined,
      experience: (data.experience || []).map((exp: any) => ({
        company: exp.company || 'Unknown Company',
        position: exp.position || exp.title || 'Professional',
        startDate: normalizeDate(exp.startDate),
        endDate: exp.current ? 'present' : normalizeDate(exp.endDate),
        description: exp.description || undefined,
        achievements: exp.achievements || undefined,
      })),
      education: (data.education || []).map((edu: any) => ({
        institution: edu.institution || 'Unknown Institution',
        degree: edu.degree || 'Degree',
        field: edu.fieldOfStudy || edu.field || undefined,
        startDate: normalizeDate(edu.startDate),
        endDate: normalizeDate(edu.endDate),
      })),
      skills: {
        technical: Array.isArray(data.skills) ? data.skills.filter((s: any) => typeof s === 'string') : [],
        soft: [],
      },
      certifications: (data.certifications || []).map((cert: any) => ({
        name: cert.name || 'Certification',
        issuer: cert.issuer || 'Unknown',
        date: normalizeDate(cert.issueDate || cert.date),
      })),
    };

    try {
      const response = await apiClient.post<ApiResponse<Profile>>('/api/v1/profile', backendData);
      const serverProfile = normalizeProfile(response.data.data as any);
      setLocalMode(false);
      setProfile(serverProfile);
      return serverProfile;
    } catch (error) {
      console.error('[ProfileService] Server profile creation failed:', error);
      // Fallback to local storage (no recursive call)
      const localProfile: Profile = normalizeProfile({
        id: generateLocalId(),
        userId: 'local-user',
        ...data,
        createdAt: new Date(),
        updatedAt: new Date(),
      } as any);
      await localProfileService.saveProfile(localProfile as any);
      setProfile(localProfile);
      // Only mark as local mode if not authenticated — authenticated users stay in
      // cloud mode so the next save attempt goes to server when it recovers.
      if (!isAuthenticated) {
        setLocalMode(true);
      }
      return localProfile;
    }
  },

  /**
   * Full profile update (local or server)
   */
  async updateProfile(data: ProfileUpdate): Promise<Profile> {
    const { mode, isAuthenticated } = useAuthStore.getState();
    const { isLocalMode, profile, setProfile } = useProfileStore.getState();

    if (!isAuthenticated && (isLocalMode || mode === 'local') && profile) {
      // Update local profile
      const updatedProfile: Profile = {
        ...profile,
        ...data,
        updatedAt: new Date(),
      };
      await localProfileService.saveProfile(updatedProfile as any);

      // Синхронизировать с extension
      console.log('🔄 [ProfileService] Syncing updated profile to extension...');
      await extensionSyncService.syncProfileToExtension(updatedProfile);

      setProfile(updatedProfile);
      return updatedProfile;
    }

    // Update on server — wrap flat fields into backend schema format
    const backendData: Record<string, unknown> = {};
    if (data.firstName !== undefined || data.lastName !== undefined || data.email !== undefined || data.phone !== undefined || data.location !== undefined) {
      const currentProfile = profile || {} as any;
      backendData.personalInfo = {
        fullName: `${data.firstName ?? currentProfile.firstName ?? ''} ${data.lastName ?? currentProfile.lastName ?? ''}`.trim() || 'Unknown',
        email: data.email ?? currentProfile.email ?? 'user@example.com',
        phone: data.phone || undefined,
        location: data.location || undefined,
      };
    }
    if (data.summary !== undefined) {
      backendData.summary = data.summary || undefined;
    }
    const response = await apiClient.put<ApiResponse<Profile>>('/api/v1/profile', backendData);
    const normalized = normalizeProfile(response.data.data as any);
    setProfile(normalized);
    return normalized;
  },

  /**
   * Partial profile update (local or server)
   */
  async patchProfile(data: Partial<ProfileUpdate>): Promise<Profile> {
    const { mode, isAuthenticated } = useAuthStore.getState();
    const { isLocalMode, profile, setProfile } = useProfileStore.getState();

    if (!isAuthenticated && (isLocalMode || mode === 'local') && profile) {
      // Update local profile
      const updatedProfile: Profile = {
        ...profile,
        ...data,
        updatedAt: new Date(),
      };
      await localProfileService.saveProfile(updatedProfile as any);

      // Синхронизировать с extension
      console.log('🔄 [ProfileService] Syncing updated profile to extension...');
      await extensionSyncService.syncProfileToExtension(updatedProfile);

      setProfile(updatedProfile);
      return updatedProfile;
    }

    // Update on server — wrap flat fields into backend schema format
    const backendData: Record<string, unknown> = {};
    if (data.firstName !== undefined || data.lastName !== undefined || data.email !== undefined || data.phone !== undefined || data.location !== undefined) {
      const currentProfile = profile || {} as any;
      backendData.personalInfo = {
        fullName: `${data.firstName ?? currentProfile.firstName ?? ''} ${data.lastName ?? currentProfile.lastName ?? ''}`.trim() || 'Unknown',
        email: data.email ?? currentProfile.email ?? 'user@example.com',
        phone: data.phone || undefined,
        location: data.location || undefined,
      };
    }
    if (data.summary !== undefined) {
      backendData.summary = data.summary || undefined;
    }
    const response = await apiClient.patch<ApiResponse<Profile>>('/api/v1/profile', backendData);
    const normalized = normalizeProfile(response.data.data as any);
    setProfile(normalized);
    return normalized;
  },

  /**
   * Delete profile (local or server)
   */
  async deleteProfile(): Promise<void> {
    const { mode, isAuthenticated } = useAuthStore.getState();
    const { isLocalMode, clearProfile, setLocalMode } = useProfileStore.getState();

    if (!isAuthenticated && (isLocalMode || mode === 'local')) {
      console.log('🗑️ [ProfileService] Deleting local profile and syncing to extension...');

      // First sync deletion to extension
      await extensionSyncService.syncProfileDeletionToExtension();

      // Then clear from Zustand
      clearProfile();
      setLocalMode(true);

      // Also delete from localStorage storage directly
      await localProfileService.deleteProfile();

      console.log('✅ [ProfileService] Local profile deleted and synced');
      return;
    }

    // Delete from server
    await apiClient.delete('/api/v1/profile');
  },

  /**
   * Get profile preview (local or server)
   */
  async getProfilePreview(): Promise<ProfilePreview> {
    const { isAuthenticated } = useAuthStore.getState();
    const { isLocalMode, profile } = useProfileStore.getState();

    if (!isAuthenticated && isLocalMode && profile) {
      // Calculate from local profile
      return {
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
        location: profile.location,
        experienceYears: profile.experience.length,
        skillsCount: profile.skills.length,
      };
    }

    // Get from server
    const response = await apiClient.get<ApiResponse<ProfilePreview>>('/api/v1/profile/preview');
    return response.data.data;
  },

  /**
   * Check if profile exists (local or server)
   */
  async checkProfileExists(): Promise<boolean> {
    const { profile } = useProfileStore.getState();

    // Check local first
    if (profile) {
      return true;
    }

    // Check server
    try {
      const response =
        await apiClient.get<ApiResponse<{ exists: boolean }>>('/api/v1/profile/exists');
      return response.data.data.exists;
    } catch {
      return false;
    }
  },

  /**
   * Update summary section (local or server)
   */
  async updateSummary(summary: string): Promise<Profile> {
    const { mode, isAuthenticated } = useAuthStore.getState();
    const { isLocalMode, profile, setProfile } = useProfileStore.getState();

    if (!isAuthenticated && (isLocalMode || mode === 'local') && profile) {
      const updated = { ...profile, summary, updatedAt: new Date() };
      await localProfileService.saveProfile(updated as any);
      setProfile(updated);
      return updated;
    }

    const response = await apiClient.put<ApiResponse<Profile>>('/api/v1/profile/summary', {
      summary,
    });
    return response.data.data;
  },

  /**
   * Update experience section (local or server)
   */
  async updateExperience(experience: Profile['experience']): Promise<Profile> {
    const { mode, isAuthenticated } = useAuthStore.getState();
    const { isLocalMode, profile, setProfile } = useProfileStore.getState();

    if (!isAuthenticated && (isLocalMode || mode === 'local') && profile) {
      const updated = { ...profile, experience, updatedAt: new Date() };
      await localProfileService.saveProfile(updated as any);
      setProfile(updated);
      return updated;
    }

    // Strip id/current, convert null endDate to 'present' for backend schema
    const normalizeDate = (d: string | null | undefined): string => {
      if (!d) return '2020-01';
      const trimmed = d.trim();
      if (/^\d{4}-\d{2}$/.test(trimmed)) return trimmed;
      if (/^\d{4}$/.test(trimmed)) return `${trimmed}-01`;
      const parsed = new Date(trimmed);
      if (!isNaN(parsed.getTime())) {
        return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}`;
      }
      return '2020-01';
    };

    const backendExperience = experience.map((exp: any) => ({
      company: exp.company || 'Unknown Company',
      position: exp.position || 'Professional',
      startDate: normalizeDate(exp.startDate),
      endDate: (exp.current || !exp.endDate || exp.endDate === null) ? 'present' : normalizeDate(exp.endDate),
      description: exp.description || undefined,
      achievements: exp.achievements || undefined,
    }));

    const response = await apiClient.put<ApiResponse<Profile>>('/api/v1/profile/experience', {
      experience: backendExperience,
    });
    return response.data.data;
  },

  /**
   * Update education section (local or server)
   */
  async updateEducation(education: Profile['education']): Promise<Profile> {
    const { mode, isAuthenticated } = useAuthStore.getState();
    const { isLocalMode, profile, setProfile } = useProfileStore.getState();

    if (!isAuthenticated && (isLocalMode || mode === 'local') && profile) {
      const updated = { ...profile, education, updatedAt: new Date() };
      await localProfileService.saveProfile(updated as any);
      setProfile(updated);
      return updated;
    }

    const response = await apiClient.put<ApiResponse<Profile>>('/api/v1/profile/education', {
      education,
    });
    return response.data.data;
  },

  /**
   * Update skills section (local or server)
   */
  async updateSkills(skills: string[]): Promise<Profile> {
    const { mode, isAuthenticated } = useAuthStore.getState();
    const { isLocalMode, profile, setProfile } = useProfileStore.getState();

    if (!isAuthenticated && (isLocalMode || mode === 'local') && profile) {
      const updated = { ...profile, skills, updatedAt: new Date() };
      await localProfileService.saveProfile(updated as any);
      setProfile(updated);
      return updated;
    }

    // Backend expects { skills: { technical?: string[], soft?: string[], languages?: string[] } }
    const response = await apiClient.put<ApiResponse<Profile>>('/api/v1/profile/skills', {
      skills: { technical: skills },
    });
    return response.data.data;
  },

  /**
   * Update certifications section (local or server)
   */
  async updateCertifications(certifications: Profile['certifications']): Promise<Profile> {
    const { mode, isAuthenticated } = useAuthStore.getState();
    const { isLocalMode, profile, setProfile } = useProfileStore.getState();

    if (!isAuthenticated && (isLocalMode || mode === 'local') && profile) {
      const updated = { ...profile, certifications, updatedAt: new Date() };
      await localProfileService.saveProfile(updated as any);
      setProfile(updated);
      return updated;
    }

    // Strip id/credentialId, rename issueDate→date, omit null expiryDate
    const normalizeCertDate = (d: string | null | undefined): string => {
      if (!d) return '2020-01';
      const trimmed = d.trim();
      if (/^\d{4}-\d{2}$/.test(trimmed)) return trimmed;
      if (/^\d{4}$/.test(trimmed)) return `${trimmed}-01`;
      const parsed = new Date(trimmed);
      if (!isNaN(parsed.getTime())) {
        return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}`;
      }
      return '2020-01';
    };

    const backendCerts = certifications.map((cert: any) => ({
      name: cert.name || 'Certification',
      issuer: cert.issuer || 'Unknown',
      date: normalizeCertDate(cert.issueDate || cert.date),
      ...(cert.expiryDate ? { expiryDate: normalizeCertDate(cert.expiryDate) } : {}),
    }));

    const response = await apiClient.put<ApiResponse<Profile>>('/api/v1/profile/certifications', {
      certifications: backendCerts,
    });
    return response.data.data;
  },

  /**
   * Sync local profile to server (requires authentication)
   */
  async syncToServer(localProfile: Profile): Promise<Profile> {
    try {
      // Convert local flat format to backend nested schema
      const normalizeDate = (d: string | null | undefined): string => {
        if (!d) return '2020-01';
        const trimmed = d.trim();
        if (/^\d{4}-\d{2}$/.test(trimmed)) return trimmed;
        if (/^\d{4}$/.test(trimmed)) return `${trimmed}-01`;
        const parsed = new Date(trimmed);
        if (!isNaN(parsed.getTime())) {
          return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}`;
        }
        return '2020-01';
      };

      const raw = localProfile as any;
      const backendData = {
        personalInfo: {
          fullName: `${raw.firstName || ''} ${raw.lastName || ''}`.trim() || 'Unknown',
          email: raw.email || 'user@example.com',
          phone: raw.phone || undefined,
          location: raw.location || undefined,
        },
        summary: raw.summary || undefined,
        experience: (raw.experience || []).map((exp: any) => ({
          company: exp.company || 'Unknown Company',
          position: exp.position || exp.title || 'Professional',
          startDate: normalizeDate(exp.startDate),
          endDate: (exp.current || !exp.endDate || exp.endDate === null) ? 'present' : normalizeDate(exp.endDate),
          description: exp.description || undefined,
          achievements: exp.achievements || undefined,
        })),
        education: (raw.education || []).map((edu: any) => ({
          institution: edu.institution || 'Unknown Institution',
          degree: edu.degree || 'Degree',
          field: edu.fieldOfStudy || edu.field || undefined,
          startDate: normalizeDate(edu.startDate),
          endDate: normalizeDate(edu.endDate),
        })),
        skills: {
          technical: Array.isArray(raw.skills) ? raw.skills.filter((s: any) => typeof s === 'string') : [],
          soft: [],
        },
        certifications: (raw.certifications || []).map((cert: any) => ({
          name: cert.name || 'Certification',
          issuer: cert.issuer || 'Unknown',
          date: normalizeDate(cert.issueDate || cert.date),
          ...(cert.expiryDate ? { expiryDate: normalizeDate(cert.expiryDate) } : {}),
        })),
      };

      const response = await apiClient.post<ApiResponse<Profile>>('/api/v1/profile', backendData);
      useProfileStore.getState().setLocalMode(false);
      return response.data.data;
    } catch (error) {
      throw new Error('Failed to sync profile to server. Please login first.');
    }
  },

  /**
   * Delete local profile
   */
  async deleteLocalProfile(): Promise<void> {
    const { isLocalMode, setProfile } = useProfileStore.getState();

    if (isLocalMode) {
      console.log('🗑️ [ProfileService] Deleting local profile...');

      // Удаляем из localStorage
      await localProfileService.deleteProfile();

      // Очищаем состояние в store
      setProfile(null);

      // Отправляем команду удаления в extension
      if (extensionSyncService.isExtensionAvailable()) {
        try {
          console.log('🔄 [ProfileService] Sending delete profile command to extension...');
          await window.chrome?.runtime?.sendMessage({
            type: 'UPDATE_PROFILE',
            data: null
          });
          console.log('✅ [ProfileService] Profile deleted from extension');
        } catch (error) {
          console.error('❌ [ProfileService] Failed to delete profile from extension:', error);
        }
      }
    } else {
      throw new Error('Cannot delete server-side profile. This only works in local mode.');
    }
  },

  /**
   * Check if local profile exists
   */
  async hasLocalProfile(): Promise<boolean> {
    return await localProfileService.hasProfile();
  },
};
